import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import type { AgenticIntroAction } from 'webapp/utils/agentic-intro';
import {
	AGENTIC_INTRO_TITLE,
	getAgenticIntroAction,
	isAgenticIntroDocument,
} from 'webapp/utils/agentic-intro';

import onWindowEvents from 'webapp/modifiers/on-window-events';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

const INTRO_URL = '/agentic-intro/index.html';

// How long after the iframe's load event the page gets to post `ready` before we treat
// it as not having booted.
const READY_TIMEOUT_MS = 10_000;

type AgenticIntroCta = Exclude<AgenticIntroAction, 'ready'>;

// `action` of the "AIWelcome screen modal cta clicked" event, per page CTA.
const TRACKING_ACTION: Record<AgenticIntroCta, string> = {
	'open-genie': 'genie',
	'book-call': 'schedule',
	close: 'close',
};

export default class OnboardingPopupAgenticIntroComponent extends PopupInnerModalComponent<PopupData.Onboarding.AgenticIntro> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('logger') declare loggerService: Service.Logger;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	introUrl = INTRO_URL;
	introTitle = AGENTIC_INTRO_TITLE;

	private isReady = false;
	private readyTimeout: number | null = null;

	// QA / preview opens (AiChatService.openAgenticIntroPopupPreview) must neither
	// consume the user's one-time show nor pollute tracking.
	get isPreview(): boolean {
		return !!this.popupData.preview;
	}

	override willDestroy(): void {
		super.willDestroy();
		this.clearReadyTimeout();
	}

	@action
	handleFrameLoad(event: Event): void {
		const iframe = event.target as HTMLIFrameElement;
		let introDocument: Document | null = null;

		try {
			introDocument = iframe.contentDocument;
		} catch {
			// cross-origin document - definitely not our page
		}

		// The intro page is a static asset nothing in the app bundle guarantees. When the
		// iframe ends up with something else (SPA shell for a 404, a blocked frame, ...)
		// we give up quietly and leave the intro unseen, so it is retried on the next visit.
		if (!isAgenticIntroDocument(introDocument)) {
			this.giveUp('intro page did not load', introDocument?.title ?? null);

			return;
		}

		// The static HTML is here, but "shown" means the page actually booted - it posts
		// `ready` once mounted (usually before this load event, since the page still has
		// images to fetch). Marking a blank frame as seen would leave the user with no way
		// out: there is no close chip.
		if (!this.isReady) {
			this.clearReadyTimeout();
			this.readyTimeout = window.setTimeout(() => {
				this.readyTimeout = null;
				this.giveUp('intro page did not become ready', introDocument?.title ?? null);
			}, READY_TIMEOUT_MS);
		}
	}

	@action
	handleMessage(event: Event, element: HTMLElement): void {
		const messageEvent = event as MessageEvent;
		const iframe = element.querySelector('iframe');

		// Guard against another same-origin browsing context posting a lookalike
		// message - only trust messages actually sent by our own iframe.
		if (messageEvent.source !== iframe?.contentWindow) {
			return;
		}

		const introAction = getAgenticIntroAction(messageEvent, window.location.origin);

		if (introAction === 'ready') {
			this.handleReady();
		} else if (introAction) {
			this.dismiss(introAction);
		}
	}

	// Being shown once is what counts as seen. Called from a DOM event only (never
	// mid-render), so writing user settings here is safe - unlike the constructor, which
	// would dirty `userSettings` during render.
	private handleReady(): void {
		this.isReady = true;
		this.clearReadyTimeout();

		if (this.isPreview) {
			return;
		}

		this.userSettingsService.hasSeenAgenticIntroPopup = true;
		this.analyticsService.event(TrackingEvent.AI_WELCOME_SCREEN_MODAL_SEEN);
	}

	// The three page CTAs are the only way out of the intro (no close chip, no ESC).
	private dismiss(introAction: AgenticIntroCta): void {
		if (!this.isPreview) {
			this.analyticsService.event(TrackingEvent.AI_WELCOME_SCREEN_MODAL_CTA_CLICKED, {
				action: TRACKING_ACTION[introAction],
			});
		}

		this.close();

		if (introAction === 'open-genie') {
			this.navigationService.transitionToRoute('genie');
		}
	}

	private giveUp(reason: string, title: string | null): void {
		this.clearReadyTimeout();
		this.loggerService.logError(`[AgenticIntro] ${reason}, retrying next visit`, {
			url: INTRO_URL,
			title,
		});
		this.close();
	}

	private clearReadyTimeout(): void {
		if (this.readyTimeout !== null) {
			window.clearTimeout(this.readyTimeout);
			this.readyTimeout = null;
		}
	}

	<template>
		<div class="content" {{onWindowEvents this.handleMessage "message"}}>
			<iframe
				class="agentic-intro__frame"
				src={{this.introUrl}}
				title={{this.introTitle}}
				{{on "load" this.handleFrameLoad}}
			></iframe>
		</div>
	</template>
}
